import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import styles from "./FileDetail.module.sass";
import { date, stringifyFilter } from "colbi_web_ui/lib/utils";
import Link from "colbi_web_ui/lib/components/Link/Link";
import Modal from "colbi_web_ui/lib/components/Modal/Modal";
import { LoadingIndicator } from "colbi_web_ui/lib/components";
import { format } from "../../utils";
import { SovosIcon } from "mosaic-react";
import { Close } from "mosaic-react-icons";

const FileDetail = (props) => {
    const { modifiers, data, modules } = props;
    const { i18n, params, history } = useGlobalValue();
    const { locale, projectId, section, fiscalYear: paramsFiscalYear } = params;
    const {
        id,
        name,
        type,
        version,
        submittedBy,
        uploadedAt,
        status,
        entitiesKpis,
        totalFindings,
        __typename,
        fileName,
        moduleByDataFileType,
        hasNavigationByImportId,
        defaultRoute,
    } = data || {};
    const prefix = data && moduleByDataFileType ? moduleByDataFileType : "";
    const invalidPrefix = `nc_audit/${prefix ? prefix : "unknown"}`;
    const removeLinks = modules ? (modules.modules || []).includes("archive") : false;
    const dataFileFiscalYear = data && data.fiscalYear;

    let fiscalYear = dataFileFiscalYear
        ? dataFileFiscalYear
        : paramsFiscalYear
          ? paramsFiscalYear
          : "";
    const currentViewMode =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("viewMode")
            : null;

    const renderActivityDetails = (showStatus = false) => (
        <>
            <div className={styles["list-header"]}>
                <span>{i18n`Information`}</span>
            </div>
            <ul>
                <li>
                    <small>{i18n`Document ID`}</small>
                    <p>{id}</p>
                </li>
                <li>
                    <small>{i18n`Name`}</small>
                    <p>{name}</p>
                </li>
            </ul>
            <ul>
                <li>
                    <small>{i18n`Document Type`}</small>
                    <p>{type}</p>
                </li>
                <li>
                    <small>{i18n`Document Version`}</small>
                    <p>{version}</p>
                </li>
            </ul>
            <ul>
                <li>
                    <small>{i18n`Uploaded By`}</small>
                    <p>{submittedBy}</p>
                </li>
                <li>
                    <small>{i18n`Uploaded At`}</small>
                    <p>{date(uploadedAt)}</p>
                </li>
            </ul>
            {showStatus && (
                <>
                    <div className={styles["list-header"]}>
                        <span>{i18n`File State`}</span>
                    </div>
                    <ul>
                        <li>
                            <small>{i18n`Status`}</small>
                            <p>{status}</p>
                        </li>
                    </ul>
                </>
            )}
        </>
    );

    const renderImportedFilesDetailsKpis = () => (
        <>
            <div>
                <div className={styles["list-header"]}>
                    <span>{i18n`Audit Result`}</span>
                    <span>{i18n`Count`}</span>
                </div>
                <ul className={styles["list-severity-summary"]}>
                    {data && totalFindings && totalFindings.length ? (
                        totalFindings
                            .sort((a, b) => b.severity.localeCompare(a.severity))
                            .map(({ severity, count }) => (
                                <li key={severity}>
                                    <small>{i18n`${severity}`}</small>
                                    <p>{format(count, "normal", null, 0) || 0}</p>
                                </li>
                            ))
                    ) : (
                        <small>{i18n`There are no results!`}</small>
                    )}
                </ul>
            </div>
            <div>
                <div className={styles["list-header"]}>
                    <span>{i18n`Content`}</span>
                    <span>{i18n`Count`}</span>
                </div>
                <ul className={styles["list-headers-summary"]}>
                    {data && entitiesKpis && entitiesKpis.length ? (
                        (entitiesKpis || []).map(({ entity, count }, index) => {
                            return (
                                <li key={index}>
                                    <small>{i18n`${entity}`}</small>
                                    <p>{format(count, "fullValue", null, 0) || 0}</p>
                                </li>
                            );
                        })
                    ) : (
                        <small>{i18n`There are no results!`}</small>
                    )}
                </ul>
            </div>
        </>
    );

    const modifiersClasses = (modifiers || [])
        .map((m) => styles[m])
        .filter((m) => m)
        .join(" ");

    const renderFoldersDetails = () => (
        <>
            {data && renderActivityDetails()}
            {data && renderImportedFilesDetailsKpis()}
        </>
    );

    const renderDetail = () => {
        switch (__typename) {
            case "InvalidDataFile":
                return renderActivityDetails(true);
            default:
                return renderFoldersDetails();
        }
    };

    const linkButton = (
        locale,
        projectId,
        fiscalYear,
        importId,
        defaultRoute,
        title,
        appendAdditionalUrlParams = false,
    ) => {
        const additionalUrlParams = appendAdditionalUrlParams
            ? `/audit_findings?filter=${stringifyFilter([
                  {
                      column: "ID",
                      filters: [{ value: data && id ? id + " | " + name : "" }],
                  },
              ])}&resetFilters=${Date.now()}`
            : "";
        const baseUrl = `/${locale}/${projectId}/${fiscalYear}/${importId}${defaultRoute ? `/${defaultRoute}` : ""}${additionalUrlParams}`;
        const viewModeQuery = currentViewMode ? `${baseUrl.includes("?") ? "&" : "?"}viewMode=${encodeURIComponent(currentViewMode)}` : "";
        const url = `${baseUrl}${viewModeQuery}`;
        return (
            <div className={styles["wrap-buttons"]}>
                <Link to={url} className={styles["close"]} label={i18n`${title}`} />
            </div>
        );
    };

    return (
        <Modal>
            <div className={styles["backdrop"]} />
            <div className={`${styles["modal-window"]}`}>
                <header className={styles["header"]}>
                    <div>
                        <button className={styles["close-button"]} onClick={() => history.goBack()}>
                            <SovosIcon>
                                <Close />
                            </SovosIcon>
                        </button>
                    </div>
                    <div>
                        <h5>{`${data ? id : ""} - ${data ? name || fileName : ""} `}</h5>
                    </div>
                </header>
                <main className={styles["main"]}>
                    <div className={`${styles["detail"]} ${modifiersClasses}`}>
                        {!data && <LoadingIndicator className={`${styles["loading-indicator"]}`} />}
                        {data &&
                            fiscalYear &&
                            fiscalYear !== "0000" &&
                            status === "imported" &&
                            !removeLinks &&
                            linkButton(
                                locale,
                                projectId,
                                fiscalYear,
                                hasNavigationByImportId ? id : "full",
                                defaultRoute ? defaultRoute : prefix,
                                "See Content",
                            )}
                        {data &&
                            fiscalYear &&
                            fiscalYear !== "0000" &&
                            status === "invalid" &&
                            !removeLinks &&
                            linkButton(
                                locale,
                                projectId,
                                fiscalYear,
                                hasNavigationByImportId ? id : "full",
                                invalidPrefix,
                                "See Audit Content",
                                true,
                            )}
                        {data &&
                            (!fiscalYear || fiscalYear === "0000") &&
                            linkButton(
                                locale,
                                projectId,
                                "0000",
                                hasNavigationByImportId ? id : "full",
                                invalidPrefix,
                                "Details",
                                true,
                            )}
                        <div className={`${styles["content"]} ${styles[section]}`}>
                            {data && renderDetail()}
                        </div>
                    </div>
                </main>
            </div>
        </Modal>
    );
};

export default FileDetail;
